import { GET_TASKS, GET_EVENTS, GET_MEETING_REQUESTS_FOR_ME, GET_CANCELED_MEETING_REQUESTS_FOR_ME, GET_CLINIC, GET_NEXT_AVAILABLE_TIME } from '@/graphql/queries/my-calendar'
import { GET_USER_SETTINGS, GET_CUSTOMER_SCHEDULE, GET_PRACTICE_EVENT_TYPES, GET_OAUTH_CONNECTION_STATUS } from '@/graphql/queries/settings'
import { GET_OR_CREATE_CALENDAR, CREATE_EVENT, UPDATE_EVENT, DELETE_EVENT, UPDATE_MEETING_REQUEST_FOR_ME, CREATE_TASK, UPDATE_TASK, DELETE_TASK, COMPLETE_TASK, UNCOMPLETE_TASK, CREATE_ZOOM_MEETING_WITHOUT_EVENT } from '@/graphql/mutations/my-calendar'
import { CREATE_PRACTICE_EVENT_TYPE, UPDATE_WORKING_HOURS, UPDATE_PRACTICE_EVENT_TYPE_COLOR, DELETE_PRACTICE_EVENT_TYPE, ADD_BATCH_SCHEDULE_EXCEPTION, DELETE_SCHEDULE_EXCEPTION, UPDATE_SCHEDULE_EXCEPTION, UPDATE_USER_BOOKING_RULES } from '@/graphql/mutations/setting'
import { transSericeApolloClient } from '@/graphql/client'
import { getTimezoneSettingApi, setTimezoneSettingCustomerApi } from '@/api/setting'
import { notify } from '@/utils/notify'
import { MOCK_EVENTS } from './mockEvents'

const USE_MOCK_EVENTS = false; // Set to false to use real API

export default {
  // Query

  async fetchEvents({ commit }, getEventsInput) {
    try {
      if (USE_MOCK_EVENTS) {
        commit('SET_RAW_EVENT_LIST', MOCK_EVENTS);
        return;
      }
      
      const { data } = await transSericeApolloClient.query({
        query: GET_EVENTS,
        variables: { input: getEventsInput },
        fetchPolicy: 'network-only'
      });
      commit('SET_RAW_EVENT_LIST', data.getEvents);
    } finally {
      commit('SET_CALENDAR_LOADING', false);
    }
  },
  async fetchClinic({ commit }, getClinicInput) {
    const { data } = await transSericeApolloClient.query({
      query: GET_CLINIC,
      variables: getClinicInput,
      fetchPolicy: 'network-only'
    });
    commit('SET_RAW_PROVIDER_LIST', data.getClinic?.clinic_customers);
    commit('SET_RAW_PATIENT_LIST', data.getClinic?.clinic_patients);
    commit('SET_RAW_LOCATION_LIST', data.getClinic?.locations);

  },
  async fetchNextAvailableTime({ commit }, getNextAvailableTimeInput) {
    const { data } = await transSericeApolloClient.query({
      query: GET_NEXT_AVAILABLE_TIME,
      variables: { input: getNextAvailableTimeInput },
      fetchPolicy: 'network-only'
    });
    commit('SET_NEXT_AVAILABLE_TIME', data.getNextAvailableTime);
  },
  async fetchMeetingRequests({ commit }) {
    const { data } = await transSericeApolloClient.query({
      query: GET_MEETING_REQUESTS_FOR_ME,
      fetchPolicy: 'network-only'
    });
    commit('SET_RAW_MEETING_REQUEST_LIST', data.getMeetingRequestsForMe);

  },
  async fetchCanceledMeetingRequests({ commit }, getCanceledMeetingRequestsInput) {
    const { data } = await transSericeApolloClient.query({
      query: GET_CANCELED_MEETING_REQUESTS_FOR_ME,
      variables: { input: getCanceledMeetingRequestsInput },
      fetchPolicy: 'network-only'
    });
    commit('SET_CANCELED_MEETING_REQUEST_LIST', data.getCanceledMeetingRequests);

  },
  async fetchTasks({ commit, state }) {
    const { data } = await transSericeApolloClient.query({
      query: GET_TASKS,
      // variables: {
      //   calendarId: state.creatorCalendarId,
      //   // todo 
      //   first: 200,
      //   after: null
      // },
      fetchPolicy: 'network-only'
    });
    // console.log(data.tasks.edges,'fetchTasks')
    commit('SET_RAW_TASK_INFO_LIST', data.tasksByCreator);

  },

  async getUserTimezone({ commit }) {
    const { data } = await getTimezoneSettingApi();
    // Only overwrite when the REST call actually returns a zone. On an empty response or a
    // failure (the GET throws), keep whatever is already loaded — a previously-fetched REST
    // value, or the browser-zone default — instead of clobbering it. This prevents a later
    // failed GET (e.g. reopening Time Zone Settings) from reverting the timezone the
    // calendar and notifications are already using.
    if (data?.timezone) {
      commit('SET_CALENDAR_TIMEZONE', data.timezone);
    }
    return data;
  },

  async getUserSettings({ commit }) {
    const { data } = await transSericeApolloClient.query({
      query: GET_USER_SETTINGS,
      fetchPolicy: 'network-only'
    });
    commit('SET_USER_SETTINGS', data.getUserSettings);
    return data.getUserSettings;
  },

  // fetch setting
  async fetchCustomerSchedule({ commit }, getCustomerScheduleInput) {
    const { data } = await transSericeApolloClient.query({
      query: GET_CUSTOMER_SCHEDULE,
      variables: { 
        clinicId: getCustomerScheduleInput.clinicId,
        customerId: getCustomerScheduleInput.customerId,
       },
      fetchPolicy: 'network-only'
    });
    commit('SET_RAW_CUSTOMER_SCHEDULE', data.getCustomerSchedule);
  },
  async fetchPracticeEventTypes({ commit }) {
    const { data } = await transSericeApolloClient.query({
      query: GET_PRACTICE_EVENT_TYPES,
      fetchPolicy: 'network-only'
    });
    commit('SET_RAW_EVENT_TYPE_LIST', data.getPracticeEventTypes);
  },

  // Mutation
  async getOrCreateCalendar({ commit }, getOrCreateCalendarInput) {
    // getOrCreateCalendarto make sure calendar_id
    const { data } = await transSericeApolloClient.mutate({
      mutation: GET_OR_CREATE_CALENDAR,
      variables: { input: getOrCreateCalendarInput }
    });
    commit('SET_CREATOR_CALENDAR_ID', data.getOrCreateCalendar.calendar_id);
    // NOTE: do NOT set the calendar timezone here. The authoritative timezone is the
    // provider's REST setting loaded via getUserTimezone (at bootstrap / settings page).
    // This action runs for provider, clinic AND patient calendars from several call sites
    // and resolves asynchronously, so committing its timezone would race with and clobber
    // the REST value — leaving meeting times/notifications on the wrong zone.
  },
  // Ensure a patient participant has a calendar_id; creates one if missing.
  // Returns the calendar_id.
  async ensurePatientCalendarId({ commit, state, rootState }, patientId) {
    if (!patientId) return null;
    const patient = (state.rawPatientList || []).find(p => p.id === patientId);
    if (patient && patient.calendar_id) return patient.calendar_id;

    const practiceId = rootState?.login?.userInfo?.clinic_id;
    const { data } = await transSericeApolloClient.mutate({
      mutation: GET_OR_CREATE_CALENDAR,
      variables: {
        input: {
          calendar_owner_id: patientId,
          practice_id: practiceId,
          role: 'patient',
        }
      }
    });
    const calendarId = data?.getOrCreateCalendar?.calendar_id || null;
    if (calendarId) {
      commit('UPDATE_RAW_PATIENT_CALENDAR_ID', { patientId, calendarId });
    }
    return calendarId;
  },
  async createEvent({ commit }, eventInput) {
    try {
      commit('SET_CALENDAR_LOADING', true);
      const { data } = await transSericeApolloClient.mutate({
        mutation: CREATE_EVENT,
        variables: { input: eventInput }
      });

      // notify message
      notify({
        title: 'Success',
        message: 'Event created successfully',
        type: 'success',
      })
      commit('SET_LAST_CREATED_EVENT_RESPONSE', data.createEvent);
      return data.createEvent;
    } catch (error) {
      console.log(error,'error')
      commit('SET_LAST_CREATED_EVENT_RESPONSE', null);
      throw error;
    } finally {
      commit('SET_CALENDAR_LOADING', false);
    }
  },
  async updateEvent({ commit }, eventInput) {
    try {
      commit('SET_CALENDAR_LOADING', true);
      const { data } = await transSericeApolloClient.mutate({
        mutation: UPDATE_EVENT,
        variables: { input: eventInput }
      });
      commit('SET_LAST_UPDATED_EVENT_RESPONSE', data.updateEvent);
      notify({
        title: 'Success',
        message: 'Event updated successfully',
        type: 'success',
      })
      return data.updateEvent;
    } catch (error) {
      commit('SET_LAST_UPDATED_EVENT_RESPONSE', null);
      throw error;
    } finally {
      commit('SET_CALENDAR_LOADING', false);
    }
  },
  async deleteEvent({ commit }, deleteEventInput) {
    try {
      const { data } = await transSericeApolloClient.mutate({
        mutation: DELETE_EVENT,
        variables: { input: deleteEventInput }
      });
      if (data?.deleteEvent?.success) {
        notify({
          title: 'Success',
          message: 'Appointment canceled successfully',
          type: 'success',
        })
      }
      return data?.deleteEvent;
    } catch (error) {
      throw error;
    }
  },
  async updateMeetingRequest({ commit }, meetingRequestInput) {
    try {
      const { data } = await transSericeApolloClient.mutate({
        mutation: UPDATE_MEETING_REQUEST_FOR_ME,
        variables: { input: meetingRequestInput }
      });
      notify({
        title: 'Success',
        message: 'Meeting request updated successfully',
        type: 'success',
      })
      return data?.updateMeetingRequestForMe;
    } catch (error) {
      throw error;
    }
  },
  async createTask({ commit }, taskInput) {
    try {
      const { data } = await transSericeApolloClient.mutate({
        mutation: CREATE_TASK,
        variables: { input: taskInput }
      });
      // console.log(data.createTask,'createTask13212321')
      // commit('SET_RAW_TASK_LIST', data.getTasks);
      commit('SET_LAST_CREATED_TASK_RESPONSE', data.createTask);
      notify({
        title: 'Success',
        message: 'Task created successfully',
        type: 'success',
      })
      return data.createTask;
    } catch (error) {
      console.log(error, 'createTask error');
      commit('SET_LAST_CREATED_TASK_RESPONSE', null);
      throw error;
    }
  },
  async updateTask({ commit }, taskInput) {
    try {
      const { task_id, ...input } = taskInput;
      const { data } = await transSericeApolloClient.mutate({
        mutation: UPDATE_TASK,
        variables: { id: task_id, input }
      });
      if (data?.updateTask) {
        commit('SET_LAST_UPDATED_TASK_RESPONSE', data.updateTask);
      }
      notify({
        title: 'Success',
        message: 'Task updated successfully',
        type: 'success',
      })
      return data?.updateTask;
    } catch (error) {
      commit('SET_LAST_UPDATED_TASK_RESPONSE', null);
      throw error;
    }
  },
  async completeTask({ commit }, { id, occurrenceDate }) {
    try {
      const variables = { id };
      if (occurrenceDate) variables.occurrenceDate = occurrenceDate;
      const { data } = await transSericeApolloClient.mutate({
        mutation: COMPLETE_TASK,
        variables: occurrenceDate ? { id, occurrenceDate } : { id }
      });
      const res = data?.completeTask;
      if (res?.success) {
        notify({
          title: 'Success',
          message: res?.message || 'Task marked as completed',
          type: 'success',
        })
      }
      return res;
    } catch (error) {
      throw error;
    }
  },
  async unCompleteTask({ commit }, { id, occurrenceDate }) {
    try {
      const variables = { id };
      if (occurrenceDate) variables.occurrenceDate = occurrenceDate;
      const { data } = await transSericeApolloClient.mutate({
        mutation: UNCOMPLETE_TASK,
        variables: occurrenceDate ? { id, occurrenceDate } : { id }
      });
      const res = data?.unCompleteTask;
      if (res?.success) {
        notify({
          title: 'Success',
          message: res?.message || 'Task marked as incomplete',
          type: 'success',
        })
      }
      return res;
    } catch (error) {
      throw error;
    }
  },
  async deleteTask({ commit }, taskId) {
    try {
      const { data } = await transSericeApolloClient.mutate({
        mutation: DELETE_TASK,
        variables: { id: taskId }
      });
      notify({
        title: 'Success',
        message: 'Task deleted successfully',
        type: 'success',
      })
      return data?.deleteTask;
    } catch (error) {
      throw error;
    }
  },
  async updateWorkingHours({ commit }, { clinicId, customerId, workingHoursInput }) {
    const { data } = await transSericeApolloClient.mutate({
      mutation: UPDATE_WORKING_HOURS,
      variables: { 
        clinicId,
        customerId,
        input: workingHoursInput 
      }
    });
    notify({
      title: 'Success',
      message: 'Working hours updated successfully',
      type: 'success',
    })
    // commit('SET_RAW_EVENT_LIST', data.getEvents);
  },
  async updatePracticeEventTypeColor({ commit }, { eventId, updatePracticeEventTypeColorInput }) {
    const { data } = await transSericeApolloClient.mutate({
      mutation: UPDATE_PRACTICE_EVENT_TYPE_COLOR,
      variables: { 
        id: eventId,
        input: updatePracticeEventTypeColorInput 
      }
    });
    notify({
      title: 'Success',
      message: 'Event type color updated successfully',
      type: 'success',
    })
    // commit('SET_RAW_EVENT_LIST', data.getEvents);
  },
  async createPracticeEventType({ commit }, createPracticeEventTypeInput) {
    const { data } = await transSericeApolloClient.mutate({
      mutation: CREATE_PRACTICE_EVENT_TYPE,
      variables: {
        input: createPracticeEventTypeInput
      }
    });
    notify({
      title: 'Success',
      message: 'Appointment type created successfully',
      type: 'success',
    })
    return data?.createPracticeEventType;
  },
  async deletePracticeEventType({ commit }, eventTypeId) {
    const { data } = await transSericeApolloClient.mutate({
      mutation: DELETE_PRACTICE_EVENT_TYPE,
      variables: { id: eventTypeId }
    });
    notify({
      title: 'Success',
      message: 'Appointment type deleted successfully',
      type: 'success',
    })
    return data?.deletePracticeEventType;
  },
  async addBatchScheduleException({ commit }, { clinicId, customerId, scheduleExceptionInput }) {
    const { data } = await transSericeApolloClient.mutate({
      mutation: ADD_BATCH_SCHEDULE_EXCEPTION,
      variables: { 
        clinicId,
        customerId,
        input: scheduleExceptionInput 
      }
    });
    notify({
      title: 'Success',
      message: 'Schedule exception added successfully',
      type: 'success',
    })
    // commit('SET_RAW_EVENT_LIST', data.getEvents);
  },
  async deleteScheduleException({ commit }, deleteExceptionInput) {
    const { data } = await transSericeApolloClient.mutate({
      mutation: DELETE_SCHEDULE_EXCEPTION,
      variables: { 
        input: deleteExceptionInput 
      }
    });
    notify({
      title: 'Success',
      message: 'Schedule exception deleted successfully',
      type: 'success',
    })
    // commit('SET_RAW_EVENT_LIST', data.getEvents);
  },
  async updateScheduleException({ commit }, updateExceptionInput) {
    const { data } = await transSericeApolloClient.mutate({
      mutation: UPDATE_SCHEDULE_EXCEPTION,
      variables: {
        input: updateExceptionInput
      }
    });
    notify({
      title: 'Success',
      message: 'Schedule exception updated successfully',
      type: 'success',
    })
    return data?.updateScheduleException;
  },

  async updateUserTimezone({ commit, state }, payload) {
    // Accept either a string (new timezone) or { timezone, old_timezone }
    const timezone = typeof payload === 'string' ? payload : payload?.timezone;
    const old_timezone =
      typeof payload === 'object' && payload?.old_timezone != null
        ? payload.old_timezone
        : state.calendarTimezone;
    const { data } = await setTimezoneSettingCustomerApi({ timezone, old_timezone });
    const newTimezone = data?.timezone || timezone;
    // Update local calendar timezone state
    commit('SET_CALENDAR_TIMEZONE', newTimezone);
    notify({
      title: 'Success',
      message: 'Timezone updated successfully',
      type: 'success',
    })
    return data;
  },

  async updateUserBookingRules({ commit }, bookingRulesInput) {
    const { data } = await transSericeApolloClient.mutate({
      mutation: UPDATE_USER_BOOKING_RULES,
      variables: {
        input: bookingRulesInput
      }
    });
    commit('SET_USER_SETTINGS', data.updateUserBookingRules);
    notify({
      title: 'Success',
      message: 'Booking rules updated successfully',
      type: 'success',
    })
    return data.updateUserBookingRules;
  },

  setCurrentEventInfo({ commit }, eventInfo) {
    // console.log('eventInfo', eventInfo)
    commit('SET_CURRENT_EVENT_INFO', eventInfo);
  },
  setCurrentTaskInfo({ commit }, taskInfo) {
    commit('SET_CURRENT_TASK_INFO', taskInfo);
  },

  // setRawEventList({ commit }, eventList) {
  //   commit('SET_RAW_EVENT_LIST', eventList);
  // },
  setTaskLoading({ commit }, loading) {
    commit('SET_TASK_LOADING', loading);
  },
  // setRawTaskInfoList({ commit }, taskInfoList) {
  //   commit('SET_RAW_TASK_INFO_LIST', taskInfoList);
  // },
  setCreateOrUpdateEventAction({ commit }, action) {
    commit('SET_CREATE_OR_UPDATE_EVENT_ACTION', action);
  },
  setPreScheduleInfo({ commit }, preScheduleInfo) {
    commit('SET_PRE_SCHEDULE_INFO', preScheduleInfo);
  },
  
  // Zoom Integration
  async checkZoomAuth({ commit }) {
    try {
      const { data } = await transSericeApolloClient.query({
        query: GET_OAUTH_CONNECTION_STATUS,
        fetchPolicy: 'network-only'
      });
      
      // Find Zoom provider status
      const zoomStatus = data.get_oauth_connection_status.find(
        provider => provider.provider === 'zoom'
      );
      
      if (zoomStatus && zoomStatus.isConnected) {
        commit('SET_ZOOM_AUTHENTICATED', true);
      } else {
        commit('SET_ZOOM_AUTHENTICATED', false);
      }
      
      return zoomStatus;
    } catch (error) {
      console.error('Error checking Zoom auth:', error);
      commit('SET_ZOOM_AUTHENTICATED', false);
      return null;
    }
  },
  
  // Creates a Zoom meeting at final submit time (no longer pre-generated on checkbox toggle).
  // Backend expects: topic (String), startTime (String, ISO), duration (Int, minutes), timezone (String).
  async createZoomMeeting({ commit }, { topic, startTime, duration, timezone } = {}) {
    try {
      const { data } = await transSericeApolloClient.mutate({
        mutation: CREATE_ZOOM_MEETING_WITHOUT_EVENT,
        variables: { topic, startTime, duration, timezone }
      });

      return data.createZoomMeetingWithoutEvent;
    } catch (error) {
      console.error('Error creating Zoom meeting:', error);
      throw error;
    }
  },
}
